import React, { useState } from 'react';
import { Save, Bell, Lock, Users, Palette, LogOut, Eye, EyeOff, Building2, UserCog, PlusCircle, BadgeCheck } from 'lucide-react';
import './Settings.css';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('account');
  const [showPassword, setShowPassword] = useState(false);
  const [settings, setSettings] = useState({
    // Account Settings
    fullName: 'Dr. Admin User',
    email: 'admin@ruamedical.com',
    phone: '+1 (555) 123-4567',
    specialization: 'General Medicine',
    licenseNumber: 'LIC-12345',
    
    // Notification Settings
    appointmentReminders: true,
    emailNotifications: true,
    smsNotifications: false,
    patientMessages: true,
    systemAlerts: true,
    
    // Privacy & Security
    twoFactorAuth: false,
    sessionTimeout: '30',
    
    // Clinic Settings
    clinicName: 'RuaMedical Clinic',
    clinicAddress: '123 Medical St, Healthcare City',
    clinicPhone: '+1 (555) 987-6543',
    appointmentDuration: '30',
    workingHours: '9:00 AM - 5:00 PM',
    
    // Preferences
    theme: 'light',
    language: 'english',
    timeFormat: '12h',
    currency: 'USD'
  });

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [clinicProfiles] = useState([
    { name: 'Dr. Admin User', role: 'Provider Admin', clinic: 'RuaMedical Downtown', status: 'Active' },
    { name: 'Mia Thompson', role: 'Practice Manager', clinic: 'RuaMedical North', status: 'Active' },
    { name: 'David Lee', role: 'Billing Lead', clinic: 'RuaMedical Telehealth Hub', status: 'Invited' },
    { name: 'Priya Patel', role: 'Scheduler', clinic: 'RuaMedical Downtown', status: 'Active' }
  ]);

  const [clinicLocations] = useState([
    { name: 'RuaMedical Downtown', city: 'Healthcare City', providers: 38, status: 'Live' },
    { name: 'RuaMedical North', city: 'North Point', providers: 27, status: 'Live' },
    { name: 'RuaMedical Telehealth Hub', city: 'Remote', providers: 59, status: 'Live' }
  ]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage your account and preferences</p>
      </div>

      <div className="settings-layout">
        {/* Sidebar Navigation */}
        <div className="settings-sidebar">
          <div className="settings-menu">
            <button
              className={`settings-menu-item ${activeTab === 'account' ? 'active' : ''}`}
              onClick={() => setActiveTab('account')}
            >
              <Users size={18} />
              Account
            </button>
            <button
              className={`settings-menu-item ${activeTab === 'notifications' ? 'active' : ''}`}
              onClick={() => setActiveTab('notifications')}
            >
              <Bell size={18} />
              Notifications
            </button>
            <button
              className={`settings-menu-item ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => setActiveTab('security')}
            >
              <Lock size={18} />
              Security
            </button>
            <button
              className={`settings-menu-item ${activeTab === 'clinic' ? 'active' : ''}`}
              onClick={() => setActiveTab('clinic')}
            >
              <Users size={18} />
              Clinic Settings
            </button>
            <button
              className={`settings-menu-item ${activeTab === 'organization' ? 'active' : ''}`}
              onClick={() => setActiveTab('organization')}
            >
              <Building2 size={18} />
              Organization
            </button>
            <button
              className={`settings-menu-item ${activeTab === 'preferences' ? 'active' : ''}`}
              onClick={() => setActiveTab('preferences')}
            >
              <Palette size={18} />
              Preferences
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="settings-content">
          {/* Account Settings Tab */}
          {activeTab === 'account' && (
            <div className="settings-section">
              <h2>Account Information</h2>
              <div className="settings-form">
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="fullName"
                    value={settings.fullName}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={settings.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email"
                  />
                </div>

                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={settings.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Specialization</label>
                    <input
                      type="text"
                      name="specialization"
                      value={settings.specialization}
                      onChange={handleInputChange}
                      placeholder="e.g., General Medicine"
                    />
                  </div>
                  <div className="form-group">
                    <label>License Number</label>
                    <input
                      type="text"
                      name="licenseNumber"
                      value={settings.licenseNumber}
                      onChange={handleInputChange}
                      placeholder="e.g., LIC-12345"
                    />
                  </div>
                </div>

                <button className="btn-save" onClick={handleSave}>
                  <Save size={18} />
                  Save Changes
                </button>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="settings-section">
              <h2>Notification Settings</h2>
              <div className="settings-toggles">
                <div className="toggle-item">
                  <div className="toggle-label">
                    <h3>Appointment Reminders</h3>
                    <p>Get notified about upcoming appointments</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      name="appointmentReminders"
                      checked={settings.appointmentReminders}
                      onChange={handleInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="toggle-item">
                  <div className="toggle-label">
                    <h3>Email Notifications</h3>
                    <p>Receive notifications via email</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      name="emailNotifications"
                      checked={settings.emailNotifications}
                      onChange={handleInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="toggle-item">
                  <div className="toggle-label">
                    <h3>SMS Notifications</h3>
                    <p>Receive notifications via SMS (may incur charges)</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      name="smsNotifications"
                      checked={settings.smsNotifications}
                      onChange={handleInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="toggle-item">
                  <div className="toggle-label">
                    <h3>Patient Messages</h3>
                    <p>Get notified of new messages from patients</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      name="patientMessages"
                      checked={settings.patientMessages}
                      onChange={handleInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="toggle-item">
                  <div className="toggle-label">
                    <h3>System Alerts</h3>
                    <p>Important system and security alerts</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      name="systemAlerts"
                      checked={settings.systemAlerts}
                      onChange={handleInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <button className="btn-save" onClick={handleSave}>
                  <Save size={18} />
                  Save Preferences
                </button>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="settings-section">
              <h2>Security Settings</h2>
              <div className="settings-form">
                <div className="security-alert">
                  <Lock size={18} />
                  <div>
                    <h3>Password Security</h3>
                    <p>Keep your account secure with a strong password</p>
                  </div>
                </div>

                <div className="form-group">
                  <label>Current Password</label>
                  <div className="password-input">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter current password"
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowPassword((prev) => !prev)}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>New Password</label>
                  <div className="password-input">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowPassword((prev) => !prev)}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Confirm Password</label>
                  <div className="password-input">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Confirm new password"
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowPassword((prev) => !prev)}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="security-section">
                  <h3>Two-Factor Authentication</h3>
                  <p>Add an extra layer of security to your account</p>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      name="twoFactorAuth"
                      checked={settings.twoFactorAuth}
                      onChange={handleInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="form-group">
                  <label>Session Timeout (minutes)</label>
                  <select
                    name="sessionTimeout"
                    value={settings.sessionTimeout}
                    onChange={handleInputChange}
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="120">2 hours</option>
                  </select>
                </div>

                <button className="btn-save" onClick={handleSave}>
                  <Save size={18} />
                  Update Security Settings
                </button>
              </div>
            </div>
          )}

          {activeTab === 'organization' && (
            <div className="settings-section">
              <h2>Organization Scale & Access</h2>

              <div className="organization-scale-cards">
                <article className="organization-scale-card">
                  <Building2 size={18} />
                  <div>
                    <h3>3 Active Clinics</h3>
                    <p>Add and manage multiple clinic locations from one dashboard.</p>
                  </div>
                </article>
                <article className="organization-scale-card">
                  <UserCog size={18} />
                  <div>
                    <h3>124 Profile Slots</h3>
                    <p>Provider, scheduler, and billing roles with scoped access.</p>
                  </div>
                </article>
                <article className="organization-scale-card">
                  <BadgeCheck size={18} />
                  <div>
                    <h3>100+ Expansion Ready</h3>
                    <p>Designed to scale to 100+ clinics and profiles.</p>
                  </div>
                </article>
              </div>

              <div className="organization-grid">
                <section className="organization-panel">
                  <div className="organization-panel-header">
                    <h3>Clinic Locations</h3>
                    <button type="button" className="btn-save organization-add-btn">
                      <PlusCircle size={16} /> Add Clinic
                    </button>
                  </div>
                  <div className="organization-list">
                    {clinicLocations.map((clinic) => (
                      <div key={clinic.name} className="organization-row">
                        <div>
                          <h4>{clinic.name}</h4>
                          <p>{clinic.city}</p>
                        </div>
                        <div className="organization-row-meta">
                          <span>{clinic.providers} Profiles</span>
                          <span className="organization-status">{clinic.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="organization-panel">
                  <div className="organization-panel-header">
                    <h3>Profiles & Access</h3>
                    <button type="button" className="btn-save organization-add-btn">
                      <PlusCircle size={16} /> Add Profile
                    </button>
                  </div>
                  <div className="organization-list">
                    {clinicProfiles.map((profile) => (
                      <div key={`${profile.name}-${profile.role}`} className="organization-row">
                        <div>
                          <h4>{profile.name}</h4>
                          <p>{profile.role}</p>
                        </div>
                        <div className="organization-row-meta">
                          <span>{profile.clinic}</span>
                          <span className="organization-status">{profile.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </div>
          )}

          {/* Clinic Settings Tab */}
          {activeTab === 'clinic' && (
            <div className="settings-section">
              <h2>Clinic Information</h2>
              <div className="settings-form">
                <div className="form-group">
                  <label>Clinic Name</label>
                  <input
                    type="text"
                    name="clinicName"
                    value={settings.clinicName}
                    onChange={handleInputChange}
                    placeholder="Enter clinic name"
                  />
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    name="clinicAddress"
                    value={settings.clinicAddress}
                    onChange={handleInputChange}
                    placeholder="Enter clinic address"
                  />
                </div>

                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="clinicPhone"
                    value={settings.clinicPhone}
                    onChange={handleInputChange}
                    placeholder="Enter clinic phone"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Appointment Duration (minutes)</label>
                    <input
                      type="number"
                      name="appointmentDuration"
                      value={settings.appointmentDuration}
                      onChange={handleInputChange}
                      min="15"
                      step="15"
                    />
                  </div>
                  <div className="form-group">
                    <label>Working Hours</label>
                    <input
                      type="text"
                      name="workingHours"
                      value={settings.workingHours}
                      onChange={handleInputChange}
                      placeholder="e.g., 9:00 AM - 5:00 PM"
                    />
                  </div>
                </div>

                <button className="btn-save" onClick={handleSave}>
                  <Save size={18} />
                  Save Clinic Info
                </button>
              </div>
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className="settings-section">
              <h2>Preferences</h2>
              <div className="settings-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Theme</label>
                    <select
                      name="theme"
                      value={settings.theme}
                      onChange={handleInputChange}
                    >
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                      <option value="auto">Auto (System)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Language</label>
                    <select
                      name="language"
                      value={settings.language}
                      onChange={handleInputChange}
                    >
                      <option value="english">English</option>
                      <option value="spanish">Spanish</option>
                      <option value="french">French</option>
                      <option value="german">German</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Time Format</label>
                    <select
                      name="timeFormat"
                      value={settings.timeFormat}
                      onChange={handleInputChange}
                    >
                      <option value="12h">12-hour (AM/PM)</option>
                      <option value="24h">24-hour</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Currency</label>
                    <select
                      name="currency"
                      value={settings.currency}
                      onChange={handleInputChange}
                    >
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                      <option value="GBP">GBP (£)</option>
                      <option value="INR">INR (₹)</option>
                    </select>
                  </div>
                </div>

                <button className="btn-save" onClick={handleSave}>
                  <Save size={18} />
                  Save Preferences
                </button>
              </div>
            </div>
          )}

          {/* Success Message */}
          {saveSuccess && (
            <div className="success-message">
              <span>✓ Settings saved successfully!</span>
            </div>
          )}

          {/* Logout Section */}
          <div className="logout-section">
            <button className="btn-logout">
              <LogOut size={18} />
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
